import { useEffect, useRef, useState } from 'react';
import {
    resolveAiObservabilityAccess,
    type AiObservabilityAccessState,
} from './aiObservabilityAccess';
import oidcAuth from '../../services/auth/oidc-client';
import { localDevAuthBypass } from '../../services/auth/localDevAuthBypass';

export const useAiObservabilityAccess = (requireManageSettings = false): AiObservabilityAccessState => {
    const [state, setState] = useState<AiObservabilityAccessState>('checking');
    const bypass = localDevAuthBypass();

    // An effect-based reset still commits one render with the previous
    // (possibly 'allowed') state before it can run, because effects fire
    // after commit. Reset synchronously during render instead, the instant
    // the policy itself changes, so a shared guard instance (e.g. sibling
    // routes reusing one component position) can never render its guarded
    // child even once under a policy it no longer satisfies.
    const prevRequireManageSettings = useRef(requireManageSettings);
    if (prevRequireManageSettings.current !== requireManageSettings) {
        prevRequireManageSettings.current = requireManageSettings;
        if (state !== 'checking') setState('checking');
    }

    useEffect(() => {
        if (bypass) return;
        let active = true;
        let generation = 0;

        // `sticky` distinguishes a renewal re-check (must not flash
        // 'checking' while already allowed, or it would unmount the guarded
        // page) from this effect's own initial run on mount AND on every
        // `requireManageSettings`/`bypass` change (must always fail closed
        // from 'checking' — a stale 'allowed' from a looser policy must
        // never carry over to a stricter one reusing this component
        // instance, e.g. sibling routes sharing a guard with no `key`).
        const refresh = (sticky: boolean) => {
            const requestGeneration = ++generation;
            setState((prev) => (sticky && prev === 'allowed' ? prev : 'checking'));
            void resolveAiObservabilityAccess(requireManageSettings)
                .then((allowed) => {
                    if (active && requestGeneration === generation) {
                        setState(allowed ? 'allowed' : 'denied');
                    }
                })
                .catch(() => {
                    if (active && requestGeneration === generation) {
                        setState('denied');
                    }
                });
        };

        refresh(false);
        const unsubscribeLoaded = oidcAuth.onUserLoaded(() => refresh(true));
        const unsubscribeInvalidated = oidcAuth.onSessionInvalidated(() => {
            generation += 1;
            if (active) setState('denied');
        });

        return () => {
            active = false;
            generation += 1;
            unsubscribeLoaded();
            unsubscribeInvalidated();
        };
    }, [bypass, requireManageSettings]);

    return bypass ? 'allowed' : state;
};
